import { useState, useEffect } from 'react'
import { useT } from '../../../../app/lib/i18n'
import type { QuickFilterConfig } from '../FilterBar.types'
import type { ColumnConfig } from '../FilterBar.types'
import { Modal } from '../../Modal'
import { SortableConfigList } from './SortableConfigList'
import * as styles from '../FilterBar.style.css'

export type ConfigTab = 'filters' | 'columns'

export interface FilterConfigModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** All available quick filters */
  quickFilters: QuickFilterConfig[]
  /** Currently selected (visible) filter ids in display order */
  selectedIds: string[]
  onSelectedIdsChange: (ids: string[]) => void
  onSave: () => void
  /** Column config for Columns tab (from Table) */
  columnConfig?: ColumnConfig
}

export function FilterConfigModal({
  open,
  onOpenChange,
  quickFilters,
  selectedIds,
  onSelectedIdsChange,
  onSave,
  columnConfig,
}: FilterConfigModalProps) {
  const t = useT()
  const [activeTab, setActiveTab] = useState<ConfigTab>('filters')

  // Filter tab: full order of all filters; visible = selected
  const [filterOrder, setFilterOrder] = useState<string[]>(() => [
    ...selectedIds,
    ...quickFilters.filter((f) => !selectedIds.includes(f.id)).map((f) => f.id),
  ])
  const [filterVisible, setFilterVisible] = useState<Set<string>>(
    () => new Set(selectedIds)
  )

  // Column tab: order and visibility (from columnConfig)
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    columnConfig ? columnConfig.columnOrder : []
  )
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => (columnConfig ? columnConfig.columnVisibility : {}))

  // Sync filter draft when modal opens or selectedIds changes
  useEffect(() => {
    if (open) {
      setFilterOrder([
        ...selectedIds,
        ...quickFilters.filter((f) => !selectedIds.includes(f.id)).map((f) => f.id),
      ])
      setFilterVisible(new Set(selectedIds))
    }
  }, [open, selectedIds, quickFilters])

  // Sync column draft when modal opens or columnConfig changes
  useEffect(() => {
    if (open && columnConfig) {
      setColumnOrder(columnConfig.columnOrder)
      setColumnVisibility(columnConfig.columnVisibility)
    }
  }, [open, columnConfig])

  const handleFilterChecked = (id: string, checked: boolean) => {
    setFilterVisible((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      const newSelected = filterOrder.filter((x) => next.has(x))
      onSelectedIdsChange(newSelected)
      return next
    })
  }

  const handleFilterOrderChange = (orderedIds: string[]) => {
    setFilterOrder(orderedIds)
    const newSelected = orderedIds.filter((id) => filterVisible.has(id))
    onSelectedIdsChange(newSelected)
  }

  const handleColumnChecked = (id: string, checked: boolean) => {
    if (!columnConfig) return
    setColumnVisibility((prev) => ({ ...prev, [id]: checked }))
  }

  const handleColumnOrderChange = (orderedIds: string[]) => {
    setColumnOrder(orderedIds)
  }

  const handleSave = () => {
    if (columnConfig) {
      columnConfig.onColumnVisibilityChange(() => columnVisibility)
      columnConfig.onColumnOrderChange(() => columnOrder)
    }
    onSave()
    onOpenChange(false)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const filterItems = filterOrder
    .map((id) => quickFilters.find((f) => f.id === id))
    .filter((f): f is QuickFilterConfig => f != null)
    .map((f) => ({
      id: f.id,
      label: f.label,
      checked: filterVisible.has(f.id),
      disabled: false,
    }))

  const columnItems = columnConfig
    ? columnOrder
        .map((id) => columnConfig.columns.find((c) => c.id === id))
        .filter((c): c is { id: string; header: string; enableHiding?: boolean } => c != null)
        .map((c) => ({
          id: c.id,
          label: c.header,
          checked: columnVisibility[c.id] !== false,
          disabled: c.enableHiding === false,
        }))
    : []

  const hasColumnsTab = columnConfig != null

  return (
    <Modal open={open} setOpen={onOpenChange} closeOnClickOff={true} darkenBackground={true}>
      <div className={styles.filterConfigModal}>
        <h2 className={styles.filterConfigModalTitle}>{t("shared.filter.configure")}</h2>

        <div className={styles.filterConfigModalTabs}>
          <button
            type="button"
            className={`${styles.filterConfigModalTab} ${activeTab === 'filters' ? styles.filterConfigModalTabActive : ''}`}
            onClick={() => setActiveTab('filters')}
          >
            {t("shared.filter.filters")}
          </button>
          {hasColumnsTab && (
            <button
              type="button"
              className={`${styles.filterConfigModalTab} ${activeTab === 'columns' ? styles.filterConfigModalTabActive : ''}`}
              onClick={() => setActiveTab('columns')}
            >
              {t("shared.filter.columns")}
            </button>
          )}
        </div>

        {activeTab === 'filters' && (
          <>
            <p className={styles.filterConfigModalDescription}>
              Select which filters to display and drag to reorder. Order in list
              matches left-to-right in the filter bar.
            </p>
            <SortableConfigList
              items={filterItems}
              onCheckedChange={handleFilterChecked}
              onOrderChange={handleFilterOrderChange}
            />
          </>
        )}

        {activeTab === 'columns' && hasColumnsTab && (
          <>
            <p className={styles.filterConfigModalDescription}>
              Select which columns to display and drag to reorder. Order in list
              matches left-to-right in the table.
            </p>
            <SortableConfigList
              items={columnItems}
              onCheckedChange={handleColumnChecked}
              onOrderChange={handleColumnOrderChange}
            />
          </>
        )}

        <div className={styles.filterConfigModalActions}>
          <button
            type="button"
            className={styles.filterConfigModalCancelButton}
            onClick={handleCancel}
          >
            {t("shared.common.cancel")}
          </button>
          <button
            type="button"
            className={styles.filterConfigModalSaveButton}
            onClick={handleSave}
          >
            {t("shared.common.save")}
          </button>
        </div>
      </div>
    </Modal>
  )
}
